import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import RootLayout from "../layout/RootLayout";
import AdminLayout from "../layout/AdminLayout";
import ProtectedAdminRoute from "./ProtectedAdminRoute";
import { useAuth } from "../context/AuthContext.jsx";
import AnalyticsTracker from "../components/analytics/AnalyticsTracker";

// Public pages
const Home = lazy(() => import("../../pages/Home"));
const Discover = lazy(() => import("../../pages/Discover"));
const ProfileDetails = lazy(() => import("../../pages/Profile/ProfileDetail"));
const Login = lazy(() => import("../../pages/Authantication/Login"));
const Register = lazy(() => import("../../pages/Authantication/Register"));
const AddProfile = lazy(() => import("../../pages/Profile/AddProfile"));
const ExploreProfiles = lazy(() => import("../../pages/ExploreProfiles"));
const PublicProfileDetails = lazy(() => import("../../pages/PublicProfileDetails"));
const Interests = lazy(() => import("../../pages/Interests"));
const ProfileMedia = lazy(() => import("../../pages/ProfileMedia"));
const MembershipPlans = lazy(() => import("../../pages/MembershipPlans"));
const PaymentStatus = lazy(() => import("../../pages/PaymentStatus"));
const HeartLoader = lazy(() => import("../../constants/HeartLoader"));
const Dashboard = lazy(() => import("../../pages/Dashboard"));
const EditUser = lazy(() => import("../../pages/EditUser"));
const EditProfile = lazy(() => import("../../pages/Profile/EditProfile"));
const Messages = lazy(() => import("../../pages/Messages"));
const Notifications = lazy(() => import("../../pages/Notifications"));
const About = lazy(() => import("../components/Footer/About"));
const PrivacyPolicy = lazy(() => import("../components/Footer/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("../components/Footer/TermsAndConditions"));
const RefundPolicy = lazy(() => import("../components/Footer/RefundPolicy"));
const Support = lazy(() => import("../components/Footer/Support"));
const PromoterForm = lazy(() => import("../../pages/Promoter/PromoterForm"));
// Admin pages
const AdminDashboard = lazy(() => import("../../components/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("../../components/admin/AdminUsers"));
const AdminProfiles = lazy(() => import("../../components/admin/AdminProfiles"));
const AdminEditUser = lazy(() => import("../../components/admin/AdminEditUser"));
const AdminEditProfile = lazy(() => import("../../components/admin/AdminEditProfile"));
const Feedback = lazy(() => import("../../pages/Feedback/Feedback"));
const ChildSafety = lazy(() => import("../../pages/ChildSafety/ChildSafety.jsx"));
const AdminSubscriptionPlans = lazy(
  () => import("../../components/admin/AdminSubscriptionPlans"),
);

function Loader() {
  return (
    <div style={{ padding: 16 }}>
      <HeartLoader />
    </div>
  );
}
function UnderDevelopmentPage() {
  return (
    <div className="up-container">
      <div className="up-card">
        <h1 className="up-heading">🚧 Page Under Development</h1>
        <p className="up-text">
          This page is currently being built and will be available soon. We’re
          working hard to bring this feature to you.
        </p>
      </div>
    </div>
  );
}

function ProtectedUserRoute() {
  const location = useLocation();
  const { authReady, loading, isAuthenticated } = useAuth();

  if (!authReady || loading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

export default function AppRoutes() {
  return (
    <>
    <AnalyticsTracker />
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Public Layout Routes */}
        <Route element={<RootLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/explore" element={<ExploreProfiles />} />
          <Route path="/:slug/details" element={<PublicProfileDetails />} />
          <Route path="/profile-media/:slug" element={<ProfileMedia />} />
          <Route path="/membership-plans" element={<MembershipPlans />} />
          <Route path="/payment-status" element={<PaymentStatus />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/404" element={<UnderDevelopmentPage />} />
          <Route path="/child-safety" element={<ChildSafety />} />
          <Route path="/loading" element={<HeartLoader />} />
          <Route path="/promoter" element={<PromoterForm />} />
          {/* Protected User Routes */}
          <Route element={<ProtectedUserRoute />}>
            <Route path="/profile-details" element={<ProfileDetails />} />
            <Route path="/add-profile" element={<AddProfile />} />
            <Route path="/interests" element={<Interests />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/edit-user" element={<EditUser />} />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/child-safety" element={<ChildSafety />} />
          </Route>
        </Route>
        {/* Protected Admin Routes */}
        <Route element={<ProtectedAdminRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId/edituser" element={<AdminEditUser />} />
            <Route path="profiles" element={<AdminProfiles />} />
            <Route path="subscription-plans" element={<AdminSubscriptionPlans />} />
            <Route
              path="profiles/:userId/editprofile" element={<AdminEditProfile />}/>
          </Route>
        </Route>
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
    </>
  );
}